var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useEffect, useMemo } from 'react';
import { Button, ScreenContainer, Typography, useTheme, } from '@pinwheel/origami';
import './ExitConfirmation.scss';
import { useSelector } from 'react-redux';
import CircledInfo from '../assets/CircledInfo';
import { useOpts } from '../context';
import { DIRECT_DEPOSIT_FORMS_EVENT_SOURCES, EventSurface, } from '../types/events';
import eventQueue from '../events/queue';
import { useGetLocalizedString } from '../hooks/useGetLocalizedString';
import useOrgName from '../hooks/useOrgName';
import useLinkNavigate from '../hooks/useLinkNavigate';
import { selectShouldShowDDFormsFallback } from '../state/slices/slice-dd-form';
import useShowingExitConfirmation from '../hooks/useShowingExitConfirmation';
import { selectLastSource } from '../state/slices/slice-segment';
import { useDDFormContext } from '../hooks/useDDFormContext';
import { checkHackyWorkspace } from '../utils/hacky-workspace-customizations';
var ExitConfirmation = function (_a) {
    var onBack = _a.onBack, onExit = _a.onExit;
    var opts = useOpts();
    var currentSource = opts.currentSource, updateEventSource = opts.updateEventSource;
    var navigate = useLinkNavigate();
    var orgName = useOrgName();
    var shouldShowDDForms = useSelector(selectShouldShowDDFormsFallback);
    var setShowingExitConfirmation = useShowingExitConfirmation().setShowingExitConfirmation;
    var lastSource = useSelector(selectLastSource);
    var setDDFormEntryPoint = useDDFormContext().setDDFormEntryPoint;
    var getLocalizedString = useGetLocalizedString();
    var resolveColor = useTheme().resolveColor;
    var linkTokenPayload = useOpts().linkTokenPayload;
    var workspaceName = linkTokenPayload === null || linkTokenPayload === void 0 ? void 0 : linkTokenPayload.workspaceName;
    var isCitadel = checkHackyWorkspace('Citadel CU', workspaceName) ||
        checkHackyWorkspace('Citadel Theme Testing', workspaceName);
    var isFlow = checkHackyWorkspace('Flow', workspaceName) ||
        checkHackyWorkspace('Flow Theme Testing', workspaceName);
    var isDoordash = checkHackyWorkspace('DoorDash', workspaceName);
    var isRobinhood = checkHackyWorkspace('Robinhood', workspaceName);
    useEffect(function () {
        if (currentSource !== EventSurface.EXIT_CONFIRMATION) {
            updateEventSource(EventSurface.EXIT_CONFIRMATION);
        }
        eventQueue.emit('impression', {});
        // Intentionally want to fire this effect only on initial load
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    var continueButtonText = useMemo(function () {
        return getLocalizedString('CONTINUE');
    }, [getLocalizedString]);
    var returnButtonText = useMemo(function () {
        return getLocalizedString('EXIT_AND_RETURN_TO_BANKAPP', [orgName]);
    }, [orgName, getLocalizedString]);
    var circleColor = resolveColor({
        type: 'theme',
        colorLegacy: 'Primary/50',
        colorNext: 'brand/accent',
    });
    var circleBorderColor = resolveColor({
        type: 'theme',
        colorLegacy: 'Primary/50',
        colorNext: 'brand/accent',
    });
    return (React.createElement(ScreenContainer, { "data-testid": "exitConfirmation", className: "exitConfirmation" },
        React.createElement("div", { className: "exitConfirmationMessage" },
            React.createElement(CircledInfo, { size: 100, color: circleColor, secondaryColor: circleBorderColor }),
            React.createElement(Typography, { variant: "header", weight: "bold", sx: { margin: '4 0 1 0' }, align: "center", color: "title", as: "h3" }, getLocalizedString('STOP_NOW')),
            React.createElement(Typography, { variant: "caption", align: "center" }, getLocalizedString('YOUR_PROGRESS_LOST'))),
        React.createElement("div", { className: "exitConfirmationActions" },
            React.createElement(Button, { onClick: onExit, dataTestId: "exitConfirmationConfirm", "aria-label": returnButtonText }, returnButtonText),
            shouldShowDDForms &&
                !DIRECT_DEPOSIT_FORMS_EVENT_SOURCES.includes(lastSource) && (React.createElement(Button, __assign({ variant: "outline" }, (isCitadel || isDoordash
                ? { color: 'secondary' }
                : isRobinhood
                    ? { color: 'neutral', variant: 'ghost' }
                    : {}), { "data-testid": "exitConfirmation-useDDForm", onClick: function () {
                    setDDFormEntryPoint('exit');
                    navigate('/dd-form/primer');
                    setShowingExitConfirmation(false);
                } }), getLocalizedString('USE_A_FORM_INSTEAD'))),
            React.createElement(Button, __assign({ onClick: onBack, dataTestId: "exitConfirmationCancel", "aria-label": continueButtonText, variant: "outline" }, (isFlow || isCitadel || isDoordash
                ? { color: 'secondary' }
                : isRobinhood
                    ? { color: 'neutral', variant: 'ghost' }
                    : {})), continueButtonText))));
};
export default ExitConfirmation;
